import { FormEvent, useEffect, useState } from "react";
import { CheckCircle2, Circle, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminUser,
  InvoiceProvider,
  InvoiceSetting,
  getCurrentAdmin,
  getInvoiceSetting,
  logout,
  updateInvoiceSetting,
} from "./api";
import { AdminRadioCardGroup, AdminSwitchCard } from "./components/AdminChoiceControl";

const PROVIDERS: InvoiceProvider[] = ["ECPay", "ezPay"];

const providerLabels: Record<InvoiceProvider, string> = {
  ECPay: "綠界科技（ECPay）電子發票",
  ezPay: "ezPay 電子發票加值服務平台",
};

const CONFIG_FIELDS: { key: "MerchantId" | "HashKey" | "HashIV"; label: string; placeholder: string }[] = [
  { key: "MerchantId", label: "商店代號（MerchantID）", placeholder: "例如 2000132" },
  { key: "HashKey", label: "HashKey", placeholder: "電子發票加值中心提供的 HashKey" },
  { key: "HashIV", label: "HashIV", placeholder: "電子發票加值中心提供的 HashIV" },
];

type FormState = {
  Provider: InvoiceProvider | "";
  MerchantId: string;
  HashKey: string;
  HashIV: string;
  Mode: "Test" | "Production";
  AutoIssueOnPaid: boolean;
  IsEnabled: boolean;
};

const emptyForm: FormState = {
  Provider: "",
  MerchantId: "",
  HashKey: "",
  HashIV: "",
  Mode: "Test",
  AutoIssueOnPaid: true,
  IsEnabled: false,
};

const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

export default function AdminInvoiceSettings() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [setting, setSetting] = useState<InvoiceSetting | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await getInvoiceSetting();
      setSetting(data.Item);
      setForm({
        Provider: data.Item.Provider || "",
        MerchantId: "",
        HashKey: "",
        HashIV: "",
        Mode: (data.Item.Mode as "Test" | "Production") || "Test",
        AutoIssueOnPaid: data.Item.AutoIssueOnPaid,
        IsEnabled: data.Item.IsEnabled,
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "電子發票設定載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function setField<Key extends keyof FormState>(key: Key, value: FormState[Key]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (form.IsEnabled && !form.Provider) {
      setMessage("請先選擇電子發票加值中心，才能開啟啟用開關。");
      return;
    }

    const config: { MerchantId?: string; HashKey?: string; HashIV?: string; Mode?: "Test" | "Production" } = { Mode: form.Mode };
    if (form.MerchantId.trim() !== "") config.MerchantId = form.MerchantId.trim();
    if (form.HashKey.trim() !== "") config.HashKey = form.HashKey.trim();
    if (form.HashIV.trim() !== "") config.HashIV = form.HashIV.trim();

    setSaving(true);
    setMessage("");
    try {
      const saved = await updateInvoiceSetting({
        Provider: form.Provider,
        Config: config,
        AutoIssueOnPaid: form.AutoIssueOnPaid,
        IsEnabled: form.IsEnabled,
      });
      setSetting(saved.Item);
      setForm((current) => ({ ...current, MerchantId: "", HashKey: "", HashIV: "" }));
      setMessage("電子發票設定已儲存。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "電子發票設定儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  const configStatus = setting?.ConfigStatus || {};

  return (
    <AdminLayout sectionLabel="帳務管理" title="電子發票設定" user={currentUser} onLogout={handleLogout}>
      <p className="mb-4 text-xs text-[#7b877f]">
        訂單付款成功後可自動向電子發票加值中心開立發票，需先向 ECPay 或 ezPay 申請電子發票加值服務帳號並取得商店代號／HashKey／HashIV
        （這組帳號與金流付款用的商店代號是各自獨立申請的，即使已經在用同一家的金流服務，電子發票仍需另外申請）。建議先在測試模式完整驗證開立／作廢流程無誤後，
        再切換正式模式並打開下方的「啟用電子發票」開關。
      </p>

      <form onSubmit={handleSubmit}>
        {message && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

        {loading ? (
          <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-10 text-center text-[#59665f]">載入中...</div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-5">
              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">電子發票加值中心</h2>
                <AdminRadioCardGroup<InvoiceProvider | "">
                  name="Provider"
                  value={form.Provider}
                  onChange={(Provider) => setField("Provider", Provider)}
                  options={PROVIDERS.map((provider) => ({ value: provider, label: providerLabels[provider] }))}
                  columnsClassName="sm:grid-cols-2"
                />
              </section>

              {form.Provider && (
                <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                  <h2 className="mb-1 text-lg font-bold text-[#203d32]">API 憑證設定</h2>
                  <p className="mb-4 text-xs text-[#9a8a7a]">欄位留空表示「保留原本已設定的值」，不會清空既有設定；正式上線前請務必先在測試模式完整跑過一次開立與作廢流程。</p>
                  <div className="grid gap-4">
                    <div className="grid gap-2">
                      <span className="text-sm font-bold text-[#315746]">環境</span>
                      <AdminRadioCardGroup<"Test" | "Production">
                        name="Mode"
                        value={form.Mode}
                        onChange={(Mode) => setField("Mode", Mode)}
                        options={[
                          { value: "Test", label: "測試模式", description: "使用加值中心提供的測試環境與測試憑證。" },
                          { value: "Production", label: "正式模式", description: "串接正式環境，開立的發票具法律效力。" },
                        ]}
                        columnsClassName="sm:grid-cols-2"
                      />
                    </div>
                    {CONFIG_FIELDS.map((field) => {
                      const isSet = !!configStatus[field.key];
                      return (
                        <label className="grid gap-2 text-sm font-bold text-[#315746]" key={field.key}>
                          <span className="flex items-center gap-1.5">
                            {isSet ? <CheckCircle2 className="text-[#2f8f5b]" size={14} /> : <Circle className="text-[#c3cdc7]" size={14} />}
                            {field.label}
                          </span>
                          <input
                            className={inputClass}
                            placeholder={isSet ? "已設定，留空維持原值" : field.placeholder}
                            value={form[field.key]}
                            onChange={(event) => setField(field.key, event.target.value)}
                          />
                        </label>
                      );
                    })}
                  </div>
                </section>
              )}

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">開立規則</h2>
                <div className="grid gap-3">
                  <AdminSwitchCard
                    checked={form.AutoIssueOnPaid}
                    description="訂單付款狀態轉為「已付款」時（金流回應或後台手動更新皆適用），自動向加值中心開立發票；關閉後需自行到訂單詳情頁手動開立。"
                    label="付款成功自動開立"
                    onChange={(AutoIssueOnPaid) => setField("AutoIssueOnPaid", AutoIssueOnPaid)}
                  />
                  <AdminSwitchCard
                    checked={form.IsEnabled}
                    description="必須完成上方憑證設定並確認無誤後才建議開啟；關閉時即使付款成功也不會呼叫加值中心（含手動開立按鈕也會停用）。"
                    label="啟用電子發票"
                    onChange={(IsEnabled) => setField("IsEnabled", IsEnabled)}
                  />
                </div>
              </section>
            </div>

            <aside className="space-y-5">
              <section className="sticky top-[76px] z-10 rounded-md border border-[#dfe6e1] bg-white p-4 md:top-20">
                <h2 className="mb-3 text-lg font-bold text-[#203d32]">儲存</h2>
                <div className="mb-3 space-y-1 text-xs text-[#9a8a7a]">
                  <p>憑證狀態：{setting?.IsConfigured ? "已完成設定" : "尚未完成設定"}</p>
                  <p>目前狀態：{setting?.IsReady ? "可正常開立發票" : "尚未就緒"}</p>
                  {setting?.UpdatedAt && <p>上次更新：{setting.UpdatedAt}</p>}
                </div>
                <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                  <Save size={18} />
                  {saving ? "儲存中" : "儲存電子發票設定"}
                </button>
              </section>
            </aside>
          </div>
        )}
      </form>
    </AdminLayout>
  );
}
